import { useMemo } from "react";
import { cycleHeatmap } from "@/lib/aggregate";
import type { BackendCycle } from "@/store/api";

interface Props {
  cycles: BackendCycle[];
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * 7×24 heatmap of cycle starts (UTC). Pure SVG. Cell intensity scales
 * linearly with the count up to the max in the dataset.
 */
export function CycleHeatmap({ cycles }: Props) {
  const grid = useMemo(() => cycleHeatmap(cycles), [cycles]);
  const max = Math.max(1, ...grid.flat());

  const cellSize = 18;
  const gap = 2;
  const labelLeftWidth = 42;
  const labelTopHeight = 22;
  const totalW = labelLeftWidth + 24 * (cellSize + gap) - gap;
  const totalH = labelTopHeight + 7 * (cellSize + gap) - gap;

  return (
    <div className="overflow-x-auto">
      <svg width={totalW} height={totalH} role="img" aria-label="Cycle heatmap">
        {/* hour-of-day labels (top) */}
        {Array.from({ length: 24 }).map((_, h) => (
          <text
            key={`h-${h}`}
            x={labelLeftWidth + h * (cellSize + gap) + cellSize / 2}
            y={labelTopHeight - 6}
            textAnchor="middle"
            fontSize={9}
            fill="hsl(var(--muted))"
          >
            {h % 3 === 0 ? `${h}h` : ""}
          </text>
        ))}
        {/* day-of-week labels (left) */}
        {DAYS.map((d, i) => (
          <text
            key={`d-${i}`}
            x={labelLeftWidth - 6}
            y={labelTopHeight + i * (cellSize + gap) + cellSize / 2 + 3}
            textAnchor="end"
            fontSize={10}
            fill="hsl(var(--muted))"
          >
            {d}
          </text>
        ))}
        {/* cells */}
        {grid.map((row, dow) =>
          row.map((count, hour) => {
            const x = labelLeftWidth + hour * (cellSize + gap);
            const y = labelTopHeight + dow * (cellSize + gap);
            const intensity = count === 0 ? 0 : 0.15 + 0.85 * (count / max);
            return (
              <rect
                key={`${dow}-${hour}`}
                x={x}
                y={y}
                width={cellSize}
                height={cellSize}
                rx={2}
                fill="hsl(var(--accent))"
                fillOpacity={intensity}
                stroke="hsl(var(--border))"
                strokeWidth={0.5}
              >
                <title>
                  {DAYS[dow]} {hour}h · {count} cycle{count === 1 ? "" : "s"}
                </title>
              </rect>
            );
          }),
        )}
      </svg>
      <div className="text-xs text-muted mt-2">
        Cell intensity scales with cycle count (max {max}). Times are UTC.
      </div>
    </div>
  );
}
